import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, ScrollView } from 'react-native';
import { YStack } from 'tamagui';

import { ScreenHeader } from '@/components/screen-header';
import { PrimaryButton } from '@/components/primary-button';
import { PageEnd } from '@/components/page-end';
import { ScreenContainer } from '@/components/screen-container';
import { space } from '@/theme/tokens.generated';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { SettingsRow, SettingsSection } from '@/components/settings-row';
import { useAuth } from '@/hooks/auth-context';
import { useActivity } from '@/hooks/use-activity';
import { auth as authClient } from '@/lib/auth';
import { nextTextSize, textSizeLabel, type TextSize } from '@/lib/text-size';
import { loadTextSize, saveTextSize } from '@/lib/text-size-storage';

// Issue #80's Settings surface -- account-level actions and status that
// used to live crammed into Profile's own footer (verification status,
// sign out, secondary destination links), which is a form for editing
// what OTHER people see, not a place for account housekeeping. Reached
// via Profile's own "Settings" link; no tab of its own (matches
// reveal-requests/safety-center's own `href: null` pattern).
//
// Rebuilt as grouped SettingsRow sections, issue #289: this screen used
// to be six centered TextLinks under three grey uppercase labels, with
// ~250px of empty ground below "Sign out" -- the founder's original
// "I don't like dead space" complaint at its actual source, since this
// idiom (bare centered links, no status, no real row height) was the
// app's dominant navigation pattern, not a one-screen issue. Real rows
// with real height kill the dead space on their own; the version count
// at the bottom (see below) is what makes the page end on purpose
// instead of trailing off into empty space.
export function Settings() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();
  const auth = useAuth();
  const [textSize, setTextSize] = useState<TextSize>('default');

  useEffect(() => {
    void loadTextSize().then(setTextSize);
  }, []);
  // Same one /me/activity poll as every other badge surface (issue #395).
  const pendingRevealRequests = useActivity().data?.summary.pendingRevealRequests ?? 0;

  // No confirmation dialog -- signing out is a low-stakes, easily
  // reversible action (same reasoning profile/index.tsx's own signOut
  // mutation documented before this moved here).
  const signOut = useMutation({
    mutationFn: () => authClient.signOut(),
  });

  // "Real person confirmed", not "Verified" -- design.md's Verification
  // model section reserves "Verified" for Tier 1 (founder-reviewed
  // selfie + ID match). Tier 0 only confirms liveness, no identity
  // claim (unchanged from this screen's prior text-link version).
  const isTier0Verified = auth.status === 'signed-up' && auth.user.tier0Status === 'tier0_passed';

  return (
    <ScreenContainer scrollUnderDock>
      <ScrollView
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ flexGrow: 1, paddingTop: space[4], paddingBottom: dockClearance, gap: space[6] }}
      >
        <ScreenHeader title="Settings" subtitle="Account, verification, and safety." onBack={() => router.back()} />

        <YStack gap="$3">
          <SettingsSection>
            <SettingsRow
              label="Verification"
              value={isTier0Verified ? 'Real person confirmed' : 'Unverified'}
              onPress={() => router.push('/verification-pending')}
            />
            <SettingsRow
              label="Reveals & connections"
              value={pendingRevealRequests > 0 ? `${pendingRevealRequests} pending` : undefined}
              onPress={() => router.push('/reveal-requests')}
            />
            <SettingsRow
              label="Text size"
              value={textSizeLabel(textSize)}
              onPress={() => {
                const next = nextTextSize(textSize);

                setTextSize(next);
                void saveTextSize(next);
              }}
            />
            <SettingsRow label="Notifications" onPress={() => router.push('/notification-settings')} />
            <SettingsRow label="Safety Center" onPress={() => router.push('/safety-center')} />
            {/* Issue #101: api.deleteBlock existed with zero call sites
                -- there was no unblock surface at all. Lives here, not
                Profile (which is for editing what others see), matching
                Safety Center's own placement as account-safety
                housekeeping. */}
            <SettingsRow label="Blocked users" onPress={() => router.push('/blocked-users')} />
          </SettingsSection>

          {/* Issue #134 AC4: "Both documents are reachable from
              Settings-or-equivalent once #87 ships" -- the one
              remaining gap once Settings itself existed. Sign-up
              already links both (sign-up/index.tsx's own Issue #134
              comment), this is the same pair reachable post-signup. */}
          <SettingsSection>
            <SettingsRow label="Terms of Service" onPress={() => router.push('/terms')} />
            <SettingsRow label="Privacy Policy" onPress={() => router.push('/privacy')} />
          </SettingsSection>

          {/* A real destructive BUTTON, not a SettingsRow -- founder,
              2026-09-19: "settings needs a better button... rather than
              just red text." Sign out is the one row on this whole
              screen that's an ACTION, not a navigational destination
              (nothing else here changes account state), and reads as
              throwaway when it's laid out identically to "Terms of
              Service" above it. component.button.destructive-bg
              (PrimaryButton's own variant="destructive", the same
              styling block-user/index.tsx's real confirm screen uses)
              is this project's own token-by-intent answer for exactly
              this: a destructive action gets the SAME weighted, filled
              treatment everywhere it appears, never colored text. */}
          <PrimaryButton
            variant="outline"
            loading={signOut.isPending}
            onPress={() => {
              if (!signOut.isPending) signOut.mutate();
            }}
          >
            {signOut.isPending ? <ActivityIndicator color="white" /> : 'Sign out'}
          </PrimaryButton>
        </YStack>

        {/* Real content below Sign out, not empty space -- issue
            #289's own "Settings needs something below Sign out so the
            page ends on purpose" ask. A plain app-version line, the
            simplest honest thing to put there (this app has no
            versioned-release surface elsewhere to link to instead). */}
        <YStack flex={1} justifyContent="flex-end">
          <PageEnd>Nearkin. Small on purpose.</PageEnd>
        </YStack>
      </ScrollView>
    </ScreenContainer>
  );
}
